import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Channel, ChannelMembers, Dm } from './api';
import { Icon } from './icons';
import { avatarStyle } from './AccountMenu';
import { AgentFace } from './AgentAvatar';
import { Rituals } from './Rituals';
import { AutoRules } from './AutoRules';

type Tab = 'about' | 'members' | 'settings';

/** R7: glass channel-details dialog (About / Members / Settings) opened from the
 *  conversation header. Reuses the already-loaded channel members + topic. */
export function ChannelDetails({
  open,
  onOpenChange,
  conversation,
  members,
  meId,
  isMuted,
  onToggleMute,
  onCopyLink,
  onAddPeople,
  onAddAgent,
  onLeave,
  onDelete,
  canDelete,
  workspaceId,
  canManage,
  initialTab = 'about',
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  conversation: Channel & Partial<Dm>;
  members: ChannelMembers | null;
  meId?: string;
  isMuted: boolean;
  onToggleMute: () => void;
  onCopyLink: () => void;
  onAddPeople: () => void;
  onAddAgent: () => void;
  onLeave: () => void;
  onDelete?: () => void;
  canDelete?: boolean;
  workspaceId?: string;
  canManage?: boolean;
  initialTab?: Tab;
}) {
  const [tab, setTab] = useState<Tab>(initialTab);
  // Jump to the requested tab each time the dialog is (re)opened.
  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);
  const priv = conversation.kind === 'private';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fx-dlg-overlay" />
        <Dialog.Content className="fx-dlg" aria-describedby={undefined}>
          <div className="fx-dlg-head">
            <span className="fx-dlg-ic"><Icon name={priv ? 'lock' : 'hash'} size={18} /></span>
            <Dialog.Title className="fx-dlg-title">{conversation.name}</Dialog.Title>
            <Dialog.Close className="fx-dlg-x" aria-label="Close"><Icon name="close" size={18} /></Dialog.Close>
          </div>
          <div className="fx-dlg-tabs" role="tablist">
            {(['about', 'members', 'settings'] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className={`fx-dlg-tab${tab === t ? ' is-active' : ''}`}
                onClick={() => setTab(t)}
              >
                {t === 'about' ? 'About' : t === 'members' ? 'Members' : 'Settings'}
              </button>
            ))}
          </div>

          <div className="fx-dlg-body">
            {tab === 'about' && (
              <>
                <div className="fx-dlg-field">
                  <div className="fx-dlg-label">Topic</div>
                  <div className="fx-dlg-value">{conversation.topic || 'No topic yet.'}</div>
                </div>
                <div className="fx-dlg-field">
                  <div className="fx-dlg-label">Type</div>
                  <div className="fx-dlg-value">{priv ? 'Private channel' : 'Public channel'}</div>
                </div>
                <div className="fx-dlg-field">
                  <div className="fx-dlg-label">Channel ID</div>
                  <div className="fx-dlg-value fx-dlg-mono">{conversation.id}</div>
                </div>
              </>
            )}

            {tab === 'members' && (
              <div className="fx-dlg-members">
                {members?.users.map((u) => (
                  <div key={u.id} className="fx-dlg-memrow">
                    <span className="fx-chhead-av" style={avatarStyle(u)}>
                      {u.avatar_url ? '' : (u.name || u.email || '?')[0]?.toUpperCase()}
                    </span>
                    <span className="fx-dlg-memname">{u.name || u.email}{u.id === meId ? ' (you)' : ''}</span>
                    <span className={`fx-presence-dot${u.presence === 'away' ? ' is-away' : ''}`} />
                  </div>
                ))}
                {members?.agents.map((a) => (
                  <div key={a.id} className="fx-dlg-memrow">
                    <span className="fx-chhead-av" style={{ backgroundColor: 'transparent' }}><AgentFace name={a.name} avatarUrl={a.avatar_url} size={24} /></span>
                    <span className="fx-dlg-memname">{a.name}</span>
                    <span className="fx-memrow-tag">agent</span>
                  </div>
                ))}
                <div className="fx-dlg-actions">
                  <button type="button" className="fx-btn" onClick={onAddPeople}><Icon name="userplus" size={15} /> Add people</button>
                  <button type="button" className="fx-btn" onClick={onAddAgent}>🤖 Add an agent</button>
                </div>
              </div>
            )}

            {tab === 'settings' && (
              <div className="fx-dlg-settings">
                <button type="button" className="fx-acct-item" onClick={onToggleMute}>
                  <Icon name="bell" size={16} /> {isMuted ? 'Unmute this channel' : 'Mute this channel'}
                </button>
                <button type="button" className="fx-acct-item" onClick={onCopyLink}>
                  <Icon name="link" size={16} /> Copy link
                </button>
                {/* Rituals (recurring prompts) hidden from the frontend for now. */}
                {conversation.kind !== 'dm' && canManage && (
                  <div style={{ padding: '12px 4px 4px' }}>
                    <div className="fx-dlg-label">Auto-membership (role &amp; field rules)</div>
                    <AutoRules channelId={conversation.id} workspaceId={workspaceId} />
                  </div>
                )}
                <div className="fx-acct-sep" />
                <button type="button" className="fx-acct-item fx-acct-signout" onClick={onLeave}>
                  <Icon name="logout" size={16} /> Leave channel
                </button>
                {conversation.kind !== 'dm' && canDelete && onDelete && (
                  <button type="button" className="fx-acct-item fx-acct-signout" onClick={onDelete}>
                    <Icon name="trash" size={16} /> Delete channel
                  </button>
                )}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
